import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { getAccessToken } from '../../utils/storage';

interface HistoricoFatura {
  id: number;
  id_cliente: number;
  valor_fatura: number | null;
  data_inicio: string;
  data_fim: string | null;
  percentual_reajuste: number | null;
  dias_vencimento: string | null;
  observacao: string | null;
  data_criacao?: string;
}

interface ClientInvoiceHistoryProps {
  idCliente: number;
  razaoSocial: string;
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const ClientInvoiceHistory: React.FC<ClientInvoiceHistoryProps> = ({ idCliente, razaoSocial }) => {
  const [historico, setHistorico] = useState<HistoricoFatura[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [novoValor, setNovoValor] = useState('');
  const [percentual, setPercentual] = useState('');
  const [diasVencimento, setDiasVencimento] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [observacao, setObservacao] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchHistorico = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAccessToken();
      const res = await axios.get(`${apiUrl}/loja/${idCliente}/faturas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistorico(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar histórico de faturas:', err);
    } finally {
      setLoading(false);
    }
  }, [idCliente, apiUrl]);

  useEffect(() => {
    if (idCliente) fetchHistorico();
  }, [idCliente, fetchHistorico]);

  const handleOpenModal = () => {
    setNovoValor('');
    setPercentual('');
    setDiasVencimento('');
    setDataInicio(new Date().toISOString().split('T')[0]);
    setObservacao('');
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSubmit = async () => {
    const token = getAccessToken();

    const payload = {
      id_cliente: idCliente,
      valor_fatura: novoValor ? parseFloat(novoValor.replace(',', '.')) : null,
      data_inicio: dataInicio ? new Date(dataInicio) : new Date(),
      percentual_reajuste: percentual ? parseFloat(percentual.replace(',', '.')) : null,
      dias_vencimento: diasVencimento || null,
      observacao: observacao || null,
    };

    try {
      await axios.post(`${apiUrl}/loja/${idCliente}/faturas/reajuste`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchHistorico();
      handleCloseModal();
    } catch (err) {
      console.error('Erro ao registrar reajuste:', err);
      alert('Erro ao registrar reajuste. Verifique o console.');
    }
  };

  const isFormValid =
    novoValor && parseFloat(novoValor.replace(',', '.')) > 0 && dataInicio;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon fontSize="small" />
          Histórico de Faturas
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
        >
          Novo Reajuste
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f7fb' }}>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Valor da Fatura</strong></TableCell>
              <TableCell><strong>Data Início</strong></TableCell>
              <TableCell><strong>Data Fim</strong></TableCell>
              <TableCell><strong>% Reajuste</strong></TableCell>
              <TableCell><strong>Vencimento</strong></TableCell>
              <TableCell><strong>Observação</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Carregando...</TableCell>
              </TableRow>
            ) : historico.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Nenhum histórico de fatura encontrado.</TableCell>
              </TableRow>
            ) : (
              historico.map((item) => {
                const isAtivo = item.data_fim === null;
                return (
                  <TableRow
                    key={item.id}
                    sx={isAtivo ? { backgroundColor: '#e8f5e9' } : {}}
                  >
                    <TableCell>
                      {isAtivo ? (
                        <Chip label="Atual" color="success" size="small" />
                      ) : (
                        <Chip label="Encerrado" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{formatCurrency(item.valor_fatura)}</TableCell>
                    <TableCell>{formatDate(item.data_inicio)}</TableCell>
                    <TableCell>{formatDate(item.data_fim)}</TableCell>
                    <TableCell>
                      {item.percentual_reajuste != null
                        ? `${item.percentual_reajuste}%`
                        : '-'}
                    </TableCell>
                    <TableCell>{item.dias_vencimento || '-'}</TableCell>
                    <TableCell>{item.observacao || '-'}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Novo Reajuste */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          Novo Reajuste — {razaoSocial}
          <IconButton
            onClick={handleCloseModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Novo Valor da Fatura *"
              type="number"
              value={novoValor}
              onChange={(e) => setNovoValor(e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              helperText="Valor mensal do cliente"
            />
            <TextField
              label="Percentual de Reajuste"
              type="number"
              value={percentual}
              onChange={(e) => setPercentual(e.target.value)}
              fullWidth
              inputProps={{ min: 0, max: 100, step: '0.01' }}
              helperText="Opcional. Ex: 10.5 para 10,5%"
            />
            <TextField
              label="Dias de Vencimento"
              value={diasVencimento}
              onChange={(e) => setDiasVencimento(e.target.value)}
              fullWidth
              placeholder="Ex: 10, 20"
              helperText="Dia(s) do mês de vencimento"
            />
            <TextField
              label="Data de Início *"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Observação"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Motivo do reajuste, notas internas..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="outlined">Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid}
          >
            Salvar Reajuste
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientInvoiceHistory;
